<template>
    <ErrorOverlay v-if="sortedClinics.length === 0" icon="fa-sharp fa-thin fa-location-dot" :title="$t('no-clinics-available')" />
    <div class="flex size-full flex-col">
        <div class="mb-3 hidden h-12 pl-5 pt-4 text-xl font-extralight text-section-title-c xl:mt-6 xl:block">{{ $t('section-title-clinic') }}</div>
        <div v-if="sortedClinics.length > 0" class="h-0 grow">
            <ScrollBar>
                <div class="mx-0 mt-2 flex flex-col xl:mx-4 xl:mt-0">
                    <div class="px-4 pb-5 xl:px-0">
                        <ClinicList v-if="sortedClinics.length > 0" :sortedClinics="sortedClinics" />
                    </div>
                </div>
            </ScrollBar>
        </div>

        <ButtonPanel
            v-if="sortedClinics.length > 0"
            :hintComplete="!!wizardStore.getSelectedClinic"
            :hintText="$t('hint_clinic')"
            :submitTitle="$t('select-title')"
            :submitDisabled="!wizardStore.getSelectedClinic"
            @submit="onSubmitClinic()"
            :loading="loading"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import ButtonPanel from '@/components/ButtonPanel.vue';
import ClinicList from '@/components/Clinics/ClinicList.vue';
import ScrollBar from '@/components/ScrollBar.vue';
import { ROUTES } from '@/enums/RoutesEnum';
import ErrorOverlay from '@/pages/Overlays/ErrorOverlay.vue';
import router from '@/router';
import { useUserStore } from '@/stores/user';
import { useWizardStore } from '@/stores/wizard';
import { loadUpConsentPlugin, registerDetailEvent, insertGtmDirectly } from '@/utils/consentHelper';

const executeOnFinishScript = () => {
    try {
        document.querySelector('#termsfeed_privacy_consent_banner_button_accept_all')?.removeEventListener('click', executeOnFinishScript);
        insertGtmDirectly(userStore.getGtmId);
    } catch (error) {
        console.error(error);
    }
};

onMounted(async () => {
    if (userStore.getGtmId) {
        loadUpConsentPlugin((resolved: boolean) => {
            document.querySelector('#termsfeed_privacy_consent_banner_button_accept_all')?.addEventListener('click', executeOnFinishScript);

            if (!resolved) {
                return;
            }
            registerDetailEvent('');
        });
    }

    wizardStore.currentStep = 0;
    window.onbeforeunload = null;
    await preselectFirstClinic();
});

const userStore = useUserStore();
const wizardStore = useWizardStore();

const sortedClinics = computed(() => {
    if (userStore.getClinics.length === 0) return [];
    // Copy the array to avoid sorting the original
    const copyClinics = [...userStore.getClinics];
    // Get only clinic with services
    return copyClinics.filter((clinic) => clinic.services && clinic.services.length > 0).sort((a, b) => a.position - b.position);
});

const loading = ref(false);

const preselectFirstClinic = async () => {
    if (!wizardStore.getSelectedClinic && sortedClinics.value.length === 1) {
        const firstClinic = sortedClinics.value[0];

        wizardStore.selectClinic({ ...firstClinic });
    }
};

const onSubmitClinic = async () => {
    if (wizardStore.getSelectedClinic === null) return;

    loading.value = true;
    wizardStore.steps[0].isValid = true;

    try {
        await router.push({ name: ROUTES.SERVICE_SELECTION });
    } catch (e) {
        console.warn(e);
    } finally {
        loading.value = false;
    }
};
</script>
